// Sugalarm — cycle 464: the onboarding "Start your free month" step.
//
// First-run order: create account → THIS → connect CGM → alarm tour → pair
// kiosk. It comes before CGM connect because, with cycle 468 enforcement
// on, an account with no service is in grace from the day it was created
// and is locked 30 days later: the free month should start before the CGM
// does, so the person never meets the lockout by surprise.
// `LoginScreen` mounts it as an ordinary step of its own column, so it is
// IN FLOW inside the frame: no portal, no overlay, no z-index, nothing it
// could cover (the cycle-453.2 rule). It renders NULL while an alarm (or a
// No Data alarm) is live, and resumes after.
//
// Shown once per ACCOUNT (the server stamps `subscription_step_done_at` on
// any exit), only for `none` / `grace`, never on the kiosk, never for
// `comped` / `trial` / `active` (`subscriptionStepOwed`). Every rail comes
// from `allowedRails`; the client never decides entitlement — a native
// purchase that resolves shows "Confirming with the store..." until the
// SERVER's answer changes.

import { useCallback, useEffect, useMemo, useState } from "react";

import { markSubscriptionStepDone, mintWebHandoff, type Subscription } from "../../api/subscription";
import {
  allowedRails,
  billingFailureMessage,
  discardExternalTab,
  getBillingSupport,
  getOffer,
  getStorefront,
  isAllowedBillingUrl,
  notNowAllowed,
  openExternal,
  prepareExternalTab,
  PRIVACY_URL,
  purchase,
  restore,
  subscriptionGrants,
  TERMS_URL,
  withBillingTimeout,
  type BillingPlatform,
  type NativeOffer,
} from "../../lib/billing";
import {
  applyServerSubscription,
  confirmWithServer,
  currentSubscriptionGeneration,
  noteSubscriptionStepDone,
  noteSubscriptionStepShowing,
  refetchSubscription,
} from "../../hooks/useSubscription";
import styles from "./SubscriptionStep.module.css";

/** Copy, verbatim from the cycle-464 prompt (no em dashes). */
export const STEP_COPY = {
  titleTrial: "Start your free month",
  titleNoTrial: "Subscribe to The Sugar Alarm",
  body: "The Sugar Alarm watches your glucose and wakes the people who need to know. Try every feature free for 1 month.",
  /** Cycle 465: the same body without the free-month sentence, for an offer
   *  with no trial (the account used its free month, or the store will not
   *  give this Apple ID one). */
  bodyNoTrial: "The Sugar Alarm watches your glucose and wakes the people who need to know.",
  priceTrial: (price: string) => `Then ${price}. Cancel anytime.`,
  priceNoTrial: (price: string) => `${price}. Cancel anytime.`,
  buttonTrial: "Start free month",
  buttonNoTrial: "Subscribe",
  restore: "Restore purchases",
  notNow: "Not now",
  confirming: "Confirming with the store...",
  renews:
    "Your subscription renews automatically each month unless canceled at least 24 hours before the end of the current period.",
  manage: "Manage or cancel in your account settings.",
} as const;

export function chargeLine(platform: BillingPlatform): string {
  if (platform === "ios") return "Payment is charged to your Apple ID account at confirmation.";
  if (platform === "android") return "Payment is charged to your Google Play account at confirmation.";
  return "Payment is charged to the card you enter at checkout.";
}

export function webLinkLabel(platform: BillingPlatform): string | null {
  if (platform === "ios") return "Or pay on thesugaralarm.com (card or Apple Pay)";
  if (platform === "android") return "Or pay on thesugaralarm.com (card or Google Pay)";
  return null;
}

interface Props {
  subscription: Subscription;
  /** The signed-in account's id: the native purchase's account token. */
  accountId: string;
  /** episode.isLive || the No Data alarm: render nothing while true. */
  alarmLive: boolean;
  /** The step is finished (any exit). The caller moves on to CGM connect. */
  onDone: () => void;
}

type Phase = "idle" | "working" | "confirming" | "unconfirmed" | "error";

export function SubscriptionStep({ subscription, accountId, alarmLive, onDone }: Props) {
  const support = useMemo(() => getBillingSupport(), []);
  const [storefront, setStorefront] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [message, setMessage] = useState<string | null>(null);

  // The alarm tour holds while the step is ON SCREEN (and only then).
  useEffect(() => {
    noteSubscriptionStepShowing(true);
    return () => noteSubscriptionStepShowing(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void getStorefront().then((s) => {
      if (!cancelled) setStorefront(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Cycle 465: on a phone that can sell, the STORE's offer is what the screen
  // shows (Apple's localized price; the free month only when the server AND
  // the store agree); the server's static descriptor stands in until it
  // arrives, and on the web.
  const [offer, setOffer] = useState<NativeOffer | null>(null);
  const nativeSelling =
    support.supported && (support.platform === "ios" || support.platform === "android");
  useEffect(() => {
    if (!nativeSelling) return;
    let cancelled = false;
    void getOffer({
      trialEligible: subscription.trial_eligible,
      priceDisplay: subscription.price_display,
    }).then((o) => {
      if (!cancelled) setOffer(o);
    });
    return () => {
      cancelled = true;
    };
  }, [nativeSelling, subscription.trial_eligible, subscription.price_display]);

  const rails = allowedRails(support.platform, storefront);
  const trial = offer?.hasTrial ?? subscription.trial_eligible;
  const price = offer?.priceDisplay ?? subscription.price_display;

  const finish = useCallback(() => {
    // Record it locally at once (the gate stops owing the step), stamp the
    // account on the server, and move on without waiting: a failed stamp
    // only means the step may be offered again on a later first run, never
    // a person held on this screen.
    noteSubscriptionStepDone();
    void markSubscriptionStepDone();
    onDone();
  }, [onDone]);

  const onPrimary = useCallback(async () => {
    setMessage(null);
    if (support.platform === "web") {
      const r = await purchase({ productId: "sugaralarm_monthly", accountToken: accountId });
      if (r.ok) {
        finish();
      } else {
        setPhase("error");
        setMessage("Couldn't open thesugaralarm.com. Try again.");
      }
      return;
    }
    setPhase("working");
    const before = subscription;
    const startedIn = currentSubscriptionGeneration();
    const current = offer ?? (await getOffer({ trialEligible: trial, priceDisplay: price }));
    if (current == null) {
      // With the plugin present the server could not offer (not configured,
      // offline); without it this archive cannot sell yet.
      setPhase("error");
      setMessage(billingFailureMessage(support.supported ? "unavailable" : "unsupported", "purchase"));
      return;
    }
    const r = await purchase({
      productId: current.productId,
      accountToken: accountId,
      appAccountToken: current.appAccountToken,
      basePlanId: current.basePlanId,
      offerId: current.offerId,
    });
    if (!r.ok) {
      // A cancellation and Ask to Buy's "pending" are decisions, not errors.
      setPhase(r.reason === "cancelled" || r.reason === "pending" ? "idle" : "error");
      setMessage(billingFailureMessage(r.reason, "purchase"));
      return;
    }
    if (r.subscription && subscriptionGrants(r.subscription)) {
      // The verify's answer IS the server's state: nothing left to poll. An
      // answer landing in another account's session is dropped by the
      // generation check and nothing advances.
      if (!applyServerSubscription(r.subscription, startedIn)) return;
      void refetchSubscription();
      finish();
      return;
    }
    setPhase("confirming");
    const confirmed = await confirmWithServer(before);
    // The poll reads whatever account is signed in NOW: a change that
    // belongs to another account's session (astra round 3) never finishes
    // this account's step.
    if (currentSubscriptionGeneration() !== startedIn) return;
    if (confirmed) {
      finish();
    } else {
      setPhase("unconfirmed");
      setMessage("We couldn't confirm it yet. If you were charged, it will show in Settings shortly.");
    }
  }, [support.platform, support.supported, accountId, subscription, offer, trial, price, finish]);

  const onRestore = useCallback(async () => {
    setMessage(null);
    setPhase("working");
    const before = subscription;
    const startedIn = currentSubscriptionGeneration();
    const r = await restore();
    if (!r.ok) {
      setPhase("error");
      setMessage(billingFailureMessage(r.reason, "restore"));
      return;
    }
    if (r.subscription && subscriptionGrants(r.subscription)) {
      // A restore of a subscription the server already had changes nothing
      // it would poll for: the verify's answer is the confirmation.
      if (!applyServerSubscription(r.subscription, startedIn)) return;
      void refetchSubscription();
      finish();
      return;
    }
    setPhase("confirming");
    const confirmed = await confirmWithServer(before);
    if (currentSubscriptionGeneration() !== startedIn) return;
    if (confirmed) {
      finish();
    } else {
      setPhase("unconfirmed");
      setMessage("The store found no active subscription for this account.");
    }
  }, [subscription, finish]);

  const onWebLink = useCallback(async () => {
    setMessage(null);
    const tab = prepareExternalTab(); // inside the tap, before any await
    setPhase("working");
    const r = await withBillingTimeout(mintWebHandoff());
    setPhase("idle");
    if (r == null || !r.ok || !isAllowedBillingUrl(r.data.url)) {
      discardExternalTab(tab);
      setPhase("error");
      setMessage("Couldn't open thesugaralarm.com. Try again.");
      return;
    }
    const opened = openExternal(r.data.url, tab);
    if (opened.ok) {
      void refetchSubscription();
      finish();
    } else {
      setPhase("error");
      setMessage("Couldn't open thesugaralarm.com. Try again.");
    }
  }, [finish]);

  // Non-interference: nothing at all while an alarm is live. State is kept,
  // so the step resumes where it was once the alarm clears.
  if (alarmLive) return null;

  const busy = phase === "working" || phase === "confirming";
  const native = support.platform === "ios" || support.platform === "android";
  const linkLabel = rails.webLink ? webLinkLabel(support.platform) : null;
  const showPrimary = rails.store != null || support.platform === "web";

  return (
    <section className={styles.root} data-testid="subscription-step" aria-labelledby="subscription-step-title">
      <h1 id="subscription-step-title" className={styles.title}>
        {trial ? STEP_COPY.titleTrial : STEP_COPY.titleNoTrial}
      </h1>
      <p className={styles.body}>{trial ? STEP_COPY.body : STEP_COPY.bodyNoTrial}</p>
      <p className={styles.price}>{trial ? STEP_COPY.priceTrial(price) : STEP_COPY.priceNoTrial(price)}</p>

      {phase === "confirming" ? (
        <p className={styles.status} role="status" aria-live="polite">
          {STEP_COPY.confirming}
        </p>
      ) : null}
      {message ? (
        <p className={styles.message} role="alert">
          {message}
        </p>
      ) : null}

      {showPrimary ? (
        <button
          type="button"
          className={styles.primary}
          data-billing-control="primary"
          disabled={busy}
          onClick={() => void onPrimary()}
        >
          {trial ? STEP_COPY.buttonTrial : STEP_COPY.buttonNoTrial}
        </button>
      ) : null}

      <div className={styles.disclosure}>
        <p>{chargeLine(support.platform)}</p>
        <p>{STEP_COPY.renews}</p>
        <p>{STEP_COPY.manage}</p>
        <p className={styles.legal}>
          <button type="button" className={styles.inlineLink} onClick={() => openExternal(TERMS_URL)}>
            Terms
          </button>
          <span aria-hidden="true"> · </span>
          <button type="button" className={styles.inlineLink} onClick={() => openExternal(PRIVACY_URL)}>
            Privacy
          </button>
        </p>
      </div>

      {native ? (
        <button
          type="button"
          className={styles.secondary}
          data-billing-control="restore"
          disabled={busy}
          onClick={() => void onRestore()}
        >
          {STEP_COPY.restore}
        </button>
      ) : null}

      {linkLabel ? (
        <button
          type="button"
          className={styles.textLink}
          data-billing-control="web-link"
          disabled={busy}
          onClick={() => void onWebLink()}
        >
          {linkLabel}
        </button>
      ) : null}

      {notNowAllowed(subscription.enforced) ? (
        <button
          type="button"
          className={styles.ghost}
          data-billing-control="not-now"
          disabled={phase === "confirming"}
          onClick={finish}
        >
          {STEP_COPY.notNow}
        </button>
      ) : null}
    </section>
  );
}

export default SubscriptionStep;
